import { applyNoDateRelease, isSolid, overlapsFirm } from '@atink/domain';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useUIStore } from '@/state/uiStore';
import { withServerAuthoredWrites } from '@/state/persistenceBridge';
import { invalidateWriteKeys } from '@/state/reactivityContract';
import { queryClient } from '@/api/queryClient';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { intentFailureCopy, type IntentFailure } from '@/api/intents';
import { rescheduleIntent, type ReleaseIntentPayload, type RescheduleIntentPayload } from '@/api/rescheduleIntent';
import { carryWaiverThroughReschedule } from '@/domain/waiverFlow';
import { sendBookingConfirmation } from '@/domain/bookingConfirmationFlow';
import type { Booking, RescheduleReason, TopupPlan } from '@/domain/types';

/**
 * Keep-original reschedule flow (Prompt G, 15 Jul ruling). Every reschedule
 * preserves the original booking (marked `superseded`) and requires a reason.
 *
 *  - `move`    — a new dated booking is created for the new slot, linked both
 *                ways, and the paid deposit is repointed to it. The new booking
 *                keeps the lifecycle status and re-enters the confirmation
 *                cadence (W4). In the served build this is ONE server
 *                transaction (POST /api/intents/reschedule, tv-2na.11.8): the
 *                store is patched optimistically WITHOUT the bridge PUTs and a
 *                refusal reverts it. A firm move onto a firm slot is refused
 *                up front (overlapsFirm), mirroring the DB exclusion.
 *  - `release` — "No date" (tv-2na.11.13): the original is preserved, no new
 *                booking is created, and the money is kept ON FILE with a fresh
 *                12-month validity — always, even inside 7 days (DECIDED 24 Sep,
 *                Q6) — so the Sweeper chases it; a top-up plan is cancelled. In
 *                the served build it is the same one intent (mode 'release').
 *
 * "Now" comes from the sim clock, never Date.now(); ids are deterministic.
 */
export type ReschedulePathway =
  | { mode: 'move'; artistId: string; newStartIso: string }
  | { mode: 'release' };

export interface RescheduleInput {
  bookingId: string;
  reason: RescheduleReason;
  /** Free-text note for the reason (required by the UI when reason is 'other'). */
  note?: string;
  pathway: ReschedulePathway;
  by: string;
  at?: string;
}

export interface RescheduleResult {
  ok: boolean;
  supersededBookingId?: string;
  /** Present on the `move` path: the new dated booking. */
  newBookingId?: string;
  /** Present on the `release` path when a paid deposit was unlinked. */
  surfacedDepositId?: string;
  error?: 'no-booking' | 'missing-reason' | 'already-superseded' | 'slot-taken';
}

/** The one staff-facing line for a move onto a slot that is (or just became) taken. */
export const SLOT_TAKEN_TITLE = 'That slot was just taken — pick another time';

function notifySlotTaken(): void {
  useUIStore.getState().pushNotification({
    intensity: 'critical',
    title: SLOT_TAKEN_TITLE,
    body: 'That artist already has a booking at that time, so nothing was moved.',
    ttlMs: 8000,
  });
}

/**
 * Would the moved booking collide with a firm booking (the DB's
 * bookings_artist_no_double_book)? Only a firm move can collide: the new row
 * keeps the original's status, so a hold never blocks or is blocked. The
 * original is excluded — it is superseded before the new row is written.
 */
function moveCollides(original: Booking, artistId: string, newStartIso: string): boolean {
  const status = original.status === 'rescheduled' ? 'confirmed' : original.status;
  if (!isSolid({ status, overbookable: original.overbookable })) return false;
  const endIso = new Date(Date.parse(newStartIso) + original.durationMin * 60_000).toISOString();
  const bookings = useBrainStore.getState().bookings;
  return overlapsFirm({ artistId, startIso: newStartIso, endIso, excludeBookingId: original.id }, bookings) !== null;
}

export function rescheduleBooking(input: RescheduleInput): RescheduleResult {
  const brain = useBrainStore.getState();
  const at = input.at ?? useSimulationStore.getState().clockIso;
  const original = brain.bookings[input.bookingId];
  if (!original) return { ok: false, error: 'no-booking' };
  // Reason is required at the type level AND guarded here, so a caller that
  // forces `undefined` through a cast still cannot commit a reasonless move.
  if (!input.reason) return { ok: false, error: 'missing-reason' };
  if (original.status === 'superseded') return { ok: false, error: 'already-superseded' };

  if (input.pathway.mode === 'move') {
    const { artistId, newStartIso } = input.pathway;
    if (moveCollides(original, artistId, newStartIso)) {
      notifySlotTaken();
      return { ok: false, error: 'slot-taken' };
    }
    const chainLen = (original.rescheduleHistory?.length ?? 0) + 1;
    const newBookingId = `bk-resched-${input.bookingId}-${chainLen}`;
    const apply = () =>
      brain.applyReschedule({
        mode: 'move',
        originalId: input.bookingId,
        newBookingId,
        artistId,
        newStartIso,
        reason: input.reason,
        note: input.note,
        by: input.by,
        at,
      });
    if (isStoreFallbackEnabled()) {
      apply();
    } else {
      viaServer(original, newBookingId, apply, {
        originalBookingId: input.bookingId,
        newBookingId,
        artistId,
        newStartIso,
        reason: input.reason,
        ...(input.note ? { note: input.note } : {}),
        by: input.by,
        at,
      });
    }
    // The waiver travels with the chain onto the new slot (I2): a signed/sent one
    // is preserved, and a move into the T-4d window with none sent dispatches now.
    carryWaiverThroughReschedule(input.bookingId, newBookingId);
    // Updated calendar invite on the new booking's confirmation (I3): same chain-
    // root UID, SEQUENCE+1. The no-date (release) branch sends none.
    sendBookingConfirmation(newBookingId);
    return { ok: true, supersededBookingId: input.bookingId, newBookingId };
  }

  // release ("No date"): the money still linked to this booking goes on file.
  const dep = original.depositId ? brain.deposits[original.depositId] : undefined;
  const surfacedDepositId =
    dep && applyNoDateRelease({ booking: original, deposit: dep, atIso: at }).depositOutcome === 'kept-on-file'
      ? dep.id
      : undefined;
  const apply = () =>
    brain.applyReschedule({
      mode: 'release',
      originalId: input.bookingId,
      reason: input.reason,
      note: input.note,
      by: input.by,
      at,
    });
  if (isStoreFallbackEnabled()) {
    apply();
  } else {
    viaServer(original, undefined, apply, {
      mode: 'release',
      originalBookingId: input.bookingId,
      reason: input.reason,
      ...(input.note ? { note: input.note } : {}),
      by: input.by,
      at,
    });
  }
  return { ok: true, supersededBookingId: input.bookingId, surfacedDepositId };
}

/** The top-up plans a move / release of this booking touches (re-pointed or cancelled). */
function plansTouchedBy(original: Booking, plans: Record<string, TopupPlan>): TopupPlan[] {
  return Object.values(plans).filter(
    (p) => p.bookingId === original.id || (original.depositId !== undefined && p.depositId === original.depositId),
  );
}

/**
 * Served build: the move (or "No date" release) is ONE server transaction
 * (POST /api/intents/reschedule). The superseded original, the new booking, the
 * re-pointed / on-file deposit and the top-up plan are mirrored into the store
 * WITHOUT the bridge PUTs (withServerAuthoredWrites — the server writes them;
 * separate PUTs are what raced the deposit FK into a 500) so the calendar and
 * Floor move at once. A refusal restores them all (still without PUTs) and tells
 * staff why. `newBookingId` is undefined for a release.
 */
function viaServer(
  original: Booking,
  newBookingId: string | undefined,
  apply: () => void,
  payload: RescheduleIntentPayload | ReleaseIntentPayload,
): void {
  const before = useBrainStore.getState();
  const depositId = original.depositId;
  const prevDeposit = depositId ? before.deposits[depositId] : undefined;
  const prevPlans = plansTouchedBy(original, before.topupPlans);
  const pairs: Array<readonly [string, string]> = [
    ['bookings', original.id],
    ...(newBookingId ? ([['bookings', newBookingId]] as const) : []),
    ...(prevDeposit ? ([['deposits', prevDeposit.id]] as const) : []),
    ...prevPlans.map((p) => ['topupPlans', p.id] as const),
  ];
  withServerAuthoredWrites(pairs, apply);

  const revert = (failure: IntentFailure) => {
    withServerAuthoredWrites(pairs, () => {
      useBrainStore.setState((s) => {
        const bookings = { ...s.bookings, [original.id]: original };
        if (newBookingId) delete bookings[newBookingId];
        const deposits = prevDeposit ? { ...s.deposits, [prevDeposit.id]: prevDeposit } : s.deposits;
        const topupPlans = { ...s.topupPlans };
        for (const p of prevPlans) topupPlans[p.id] = p;
        const cust = s.customers[original.customerId];
        const customers =
          newBookingId && cust && cust.bookings.includes(newBookingId)
            ? { ...s.customers, [cust.id]: { ...cust, bookings: cust.bookings.filter((id) => id !== newBookingId) } }
            : s.customers;
        return { bookings, deposits, customers, topupPlans };
      });
    });
    invalidateWriteKeys(queryClient, 'bookings', original);
    if (failure.kind === 'slot_taken') {
      notifySlotTaken();
      return;
    }
    const retry = intentFailureCopy(failure).retry;
    const [verb, done] = newBookingId ? ['move', 'moved'] : ['release', 'released'];
    useUIStore.getState().pushNotification({
      intensity: 'critical',
      title: `Couldn't ${verb} the booking`,
      body: retry
        ? `We couldn't reach the server, so nothing was ${done}. Try the ${verb} again.`
        : `The server refused the ${verb}: ${failure.message ?? failure.code ?? failure.error}. Nothing was ${done}.`,
      ttlMs: 10000,
    });
  };

  void rescheduleIntent(payload)
    .then((res) => {
      if (!res.ok) revert(res);
    })
    .catch((err) => {
      console.error('[reschedule] send failed', err);
      revert({ ok: false, kind: 'transient', status: 0, error: 'network' });
    });
}
